/**
 * FormInputStylesControls — shared InspectorControls panel for form input blocks.
 * Provides color pickers for input, label, placeholder, hover, and focus states.
 * Also exports getInputStyleVars() to convert attribute to CSS custom properties.
 */

import { PanelColorSettings } from '@wordpress/block-editor';
import { PanelBody, RangeControl } from '@wordpress/components';
import { __ } from '@wordpress/i18n';

/**
 * Convert inputStyles attribute to CSS custom property object.
 * Used by both Edit and Save via shared Content component.
 */
export function getInputStyleVars( inputStyles ) {
	if ( ! inputStyles ) return {};

	const map = {
		inputBg: '--fi-input-bg',
		inputColor: '--fi-input-color',
		inputBorderColor: '--fi-input-border',
		inputBorderRadius: '--fi-input-radius',
		labelColor: '--fi-label-color',
		placeholderColor: '--fi-placeholder',
		hoverBg: '--fi-hover-bg',
		hoverBorderColor: '--fi-hover-border',
		focusBg: '--fi-focus-bg',
		focusBorderColor: '--fi-focus-border',
	};

	const vars = {};
	Object.entries( map ).forEach( ( [ key, cssVar ] ) => {
		if ( inputStyles[ key ] ) {
			vars[ cssVar ] =
				key === 'inputBorderRadius'
					? inputStyles[ key ] + 'px'
					: inputStyles[ key ];
		}
	} );
	return vars;
}

/**
 * InspectorControls panel for form input styling.
 */
export default function FormInputStylesControls( {
	attributes,
	setAttributes,
} ) {
	const s = ( attributes.style && attributes.style.inputStyles ) || {};

	const update = ( key, value ) => {
		const updated = { ...s };
		if ( value ) {
			updated[ key ] = value;
		} else {
			delete updated[ key ];
		}
		setAttributes( {
			style: {
				...( attributes.style || {} ),
				inputStyles:
					Object.keys( updated ).length > 0 ? updated : undefined,
			},
		} );
	};

	return (
		<>
			<PanelColorSettings
				title={ __( 'Input Colors', 'brandy-blocks' ) }
				initialOpen={ false }
				enableAlpha
				colorSettings={ [
					{
						value: s.inputBg,
						onChange: ( v ) => update( 'inputBg', v ),
						label: __( 'Input background', 'brandy-blocks' ),
					},
					{
						value: s.inputColor,
						onChange: ( v ) => update( 'inputColor', v ),
						label: __( 'Input text', 'brandy-blocks' ),
					},
					{
						value: s.inputBorderColor,
						onChange: ( v ) => update( 'inputBorderColor', v ),
						label: __( 'Input border', 'brandy-blocks' ),
					},
					{
						value: s.labelColor,
						onChange: ( v ) => update( 'labelColor', v ),
						label: __( 'Label', 'brandy-blocks' ),
					},
					{
						value: s.placeholderColor,
						onChange: ( v ) => update( 'placeholderColor', v ),
						label: __( 'Placeholder', 'brandy-blocks' ),
					},
				] }
			/>
			<PanelBody
				title={ __( 'Input Border Radius', 'brandy-blocks' ) }
				initialOpen={ false }
			>
				<RangeControl
					label={ __( 'Radius (px)', 'brandy-blocks' ) }
					value={
						s.inputBorderRadius
							? parseInt( s.inputBorderRadius )
							: undefined
					}
					onChange={ ( v ) =>
						update(
							'inputBorderRadius',
							v != null ? String( v ) : undefined
						)
					}
					min={ 0 }
					max={ 30 }
					step={ 1 }
					__nextHasNoMarginBottom
				/>
			</PanelBody>
			<PanelColorSettings
				title={ __( 'Input Hover & Focus', 'brandy-blocks' ) }
				initialOpen={ false }
				enableAlpha
				colorSettings={ [
					{
						value: s.hoverBg,
						onChange: ( v ) => update( 'hoverBg', v ),
						label: __( 'Hover background', 'brandy-blocks' ),
					},
					{
						value: s.hoverBorderColor,
						onChange: ( v ) => update( 'hoverBorderColor', v ),
						label: __( 'Hover border', 'brandy-blocks' ),
					},
					{
						value: s.focusBg,
						onChange: ( v ) => update( 'focusBg', v ),
						label: __( 'Focus background', 'brandy-blocks' ),
					},
					{
						value: s.focusBorderColor,
						onChange: ( v ) => update( 'focusBorderColor', v ),
						label: __( 'Focus border', 'brandy-blocks' ),
					},
				] }
			/>
		</>
	);
}
